import React, { useRef } from 'react';
import { Download, Printer } from 'lucide-react';
import html2pdf from 'html2pdf.js';
import { useLanguage } from '../context/LanguageContext';

const DhvajarohanSuchana26Jan2026Form = () => {
  const { language } = useLanguage();
  const componentRef = useRef();

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPdf = () => {
    const element = componentRef.current;
    const opt = {
      margin: 0,
      filename: language === 'marathi' ? 'Dhvajarohan_Suchana_26Jan2026.pdf' : 'Flag_Hoisting_Notice_26Jan2026.pdf',
      image: { type: 'jpeg', quality: 1 },
      html2canvas: { scale: 3, useCORS: true, logging: false },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save();
  };

  return (
    <div className="min-h-screen bg-gray-200 py-8 font-sans print:py-0 print:bg-white flex flex-col items-center">
      <style>
        {`
          @media print {
            @page {
              size: A4 portrait;
              margin: 0mm;
            }
            body {
              background-color: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      
      {/* Controls Container */}
      <div className="w-full max-w-[210mm] mb-6 flex justify-end space-x-4 print:hidden px-4">
        <button
          onClick={handlePrint}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm transition-colors"
        >
          <Printer className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Print' : 'Print'}
        </button>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 shadow-sm transition-colors"
        >
          <Download className="w-4 h-4 mr-2" />
          {language === 'marathi' ? 'Download PDF' : 'Download PDF'}
        </button>
      </div>

      <div className="w-full overflow-x-auto flex justify-center">
        <div ref={componentRef} className="bg-white text-black shadow-lg print:shadow-none relative" style={{ width: '210mm' }}>
          
          <div className="px-14 py-16 mx-auto relative box-border flex flex-col" style={{ width: '210mm', minHeight: '297mm', maxHeight: '297mm', overflow: 'hidden' }}>
            
            <div className="text-center mb-10">
              <h1 className="text-2xl font-bold border-b-2 border-black inline-block pb-1">
                {language === 'marathi' ? 'जाहीर प्रगटन/सुचना' : 'Public Notice/Announcement'}
              </h1>
            </div>

            <div className="flex justify-end text-[16px] mb-8">
               <div className="flex flex-col space-y-1 w-[320px]">
                  <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? 'ग्रामपंचायत कार्यालय' : 'Gram Panchayat Office'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="mr-1">{language === 'marathi' ? 'ता.' : 'Tal.'}</span>
                    <span className="border-b border-dashed border-black w-32"></span>
                    <span className="mx-1">{language === 'marathi' ? 'जि.' : 'Dist.'}</span>
                    <span className="border-b border-dashed border-black flex-grow"></span>
                  </p>
                  <p className="flex items-end">
                    <span className="mr-2 font-bold">{language === 'marathi' ? 'दिनांक:' : 'Date:'}</span>
                    <span className="border-b border-dashed border-black w-10"></span>
                    <span>{language === 'marathi' ? '/०१/२०२६' : '/01/2026'}</span>
                  </p>
               </div>
            </div>

            <div className="text-[16px] mb-8 leading-loose">
               <p>{language === 'marathi' ? 'प्रति,' : 'To,'}</p>
               <p className="flex items-end">
                  <span className="mr-2">{language === 'marathi' ? 'सन्माननीय उपसरपंच/सदस्य/ग्रामस्थ' : 'Hon. Deputy Sarpanch/Member/Villagers'}</span>
                  <span className="border-b border-dashed border-black w-56"></span>,
               </p>
               <p className="flex items-end">
                  <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                  <span className="border-b border-dashed border-black w-80"></span>
               </p>
            </div>

            <div className="text-center mb-8">
               <h2 className="text-lg font-bold">
                 {language === 'marathi' ? 'विषय : २६ जानेवारी २०२६ रोजीच्या ध्वजारोहणास उपस्थित राहणेबाबत.' : 'Subject : Regarding presence for flag hoisting on 26th January 2026.'}
               </h2>
            </div>

            <div className="text-[16px] leading-loose">
               <p className="font-bold mb-2">{language === 'marathi' ? 'महोदय/महोदया,' : 'Respected Sir/Madam,'}</p>
               <p className="indent-12 text-justify">
                  {language === 'marathi' ? (
                    <>
                      उपरोक्त विषयान्वये आपणांस कळविण्यात येते की, सोमवार दिनांक <span className="font-bold">२६ जानेवारी २०२६</span> रोजी प्रजासत्ताक दिनानिमित्त ग्रामपंचायत कार्यालय <span className="inline-block border-b border-dashed border-black w-[350px]"></span> येथे ध्वजारोहण कार्यक्रमाचे आयोजन सकाळी ठीक <span className="inline-block border-b border-dashed border-black w-48"></span> वाजता करण्यात आलेले आहे.
                    </>
                  ) : (
                    <>
                      With reference to the above subject, this is to inform you that on Monday, <span className="font-bold">26th January 2026</span>, on the occasion of Republic Day, a flag hoisting program has been organized at Gram Panchayat Office <span className="inline-block border-b border-dashed border-black w-[350px]"></span> exactly at <span className="inline-block border-b border-dashed border-black w-48"></span> AM.
                    </>
                  )}
               </p>
               <p className="indent-12 text-justify mt-4">
                  {language === 'marathi' ? 'करिता आपण ध्वजारोहण कार्यक्रमासाठी उपस्थित राहावे, ही विनंती.' : 'Therefore, you are requested to be present for the flag hoisting program.'}
               </p>
            </div>

            {/* Signatures Area */}
            <div className="flex justify-end mt-40 text-[16px]">
               <div className="flex flex-col items-center w-[400px]">
                  <div className="flex justify-between w-full mb-6 px-4">
                     <p className="font-bold">{language === 'marathi' ? 'ग्रामपंचायत अधिकारी' : 'Gram Panchayat Officer'}</p>
                     <p className="font-bold">{language === 'marathi' ? 'सरपंच' : 'Sarpanch'}</p>
                  </div>
                  <div className="flex items-end w-full">
                     <span className="mr-2">{language === 'marathi' ? 'ग्रामपंचायत' : 'Gram Panchayat'}</span>
                     <span className="border-b border-dashed border-black flex-grow"></span>
                  </div>
               </div>
            </div>

            <div className="absolute bottom-10 left-14 text-blue-700 text-sm underline print:hidden">
              www.majhigrampanchayat.com
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DhvajarohanSuchana26Jan2026Form;
